import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../constants/routes';

/**
 * HomePage component
 * Landing page with options for existing tenants and prospective renters
 */
const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [hoveredOption, setHoveredOption] = useState<string | null>(null);

  const handleOptionClick = (option: 'existing' | 'looking') => {
    if (option === 'existing') {
      navigate(ROUTES.LOGIN);
    } else if (option === 'looking') {
      navigate(ROUTES.PROPERTY_SEARCH);
    }
  };

  return (
    <div className="entry-options-container">
      <div className="entry-options-content">
        <div className="entry-header">
          <h1 className="entry-title">Welcome to Ramirez Rentals</h1>
          <p className="entry-subtitle">Choose your path to get started</p>
        </div>

        <div className="entry-options-grid">
          {/* Existing Tenant Option */}
          <div
            className={`entry-option ${hoveredOption === 'existing' ? 'entry-option-hovered' : ''}`}
            onMouseEnter={() => setHoveredOption('existing')}
            onMouseLeave={() => setHoveredOption(null)}
            onClick={() => handleOptionClick('existing')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleOptionClick('existing');
              }
            }}
          >
            <div className="entry-option-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2 className="entry-option-title">Already Renting?</h2>
            <p className="entry-option-description">
              Current tenants can access their dashboard to make payments, view lease details, and manage their account.
            </p>
            <div className="entry-option-features">
              <div className="entry-feature">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <polyline points="20,6 9,17 4,12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Online rent payments
              </div>
              <div className="entry-feature">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <polyline points="20,6 9,17 4,12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Payment history
              </div>
              <div className="entry-feature">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <polyline points="20,6 9,17 4,12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Account management
              </div>
            </div>
            <div className="entry-option-cta">
              Sign In or Create Account
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line x1="7" y1="17" x2="17" y2="7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <polyline points="7,7 17,7 17,17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>

          {/* Looking to Rent Option */}
          <div
            className={`entry-option ${hoveredOption === 'looking' ? 'entry-option-hovered' : ''}`}
            onMouseEnter={() => setHoveredOption('looking')}
            onMouseLeave={() => setHoveredOption(null)}
            onClick={() => handleOptionClick('looking')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleOptionClick('looking');
              }
            }}
          >
            <div className="entry-option-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
                <path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2 className="entry-option-title">Looking to Rent?</h2>
            <p className="entry-option-description">
              Explore our available properties and schedule viewings to find your perfect home.
            </p>
            <div className="entry-option-features">
              <div className="entry-feature">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <polyline points="20,6 9,17 4,12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Browse properties
              </div>
              <div className="entry-feature">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <polyline points="20,6 9,17 4,12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Schedule viewings
              </div>
              <div className="entry-feature">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <polyline points="20,6 9,17 4,12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Virtual tours
              </div>
            </div>
            <div className="entry-option-cta">
              View Available Properties
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line x1="7" y1="17" x2="17" y2="7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <polyline points="7,7 17,7 17,17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
        </div>

        <div className="entry-footer">
          <p className="entry-footer-text">
            Need help? Call us at <a href="tel:555-555-5555" className="entry-footer-link">555-555-5555</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
